// The POP Tag history for one lift (Session AMZ, then called "Tapping For Tracking"; renamed to the
// Guv POP Tag in Session AND). The office table that sits under the lift's internal POP Tag card:
// every tap the motor room tag has recorded, newest first, in the server's own pages of 20.
//
// WHAT THIS TABLE IS FOR, which decides almost every choice below: the office is cross-checking it
// against the paper log card in the motor room. So:
//
//  - A VOIDED TAP IS SHOWN, struck through, with its reason. It is never hidden. A row that
//    silently disappears is exactly what makes a cross-check worthless, and "why is this one not a
//    real visit" is the question the reason field exists to answer.
//  - A TAP WITH NO SIGNED-IN USER READS "Not signed in", and that is an ORDINARY tap, not a fault.
//    A tap records WHEN somebody attended; the wet-signed log card records WHO, as it always has.
//    The note under the table says so, so nobody reads a column of blanks as a broken feature.
//  - VOIDING IS THE ONLY WAY A PERSON REMOVES A TAP (there is no delete route, by design: a tap is
//    a record of a real event). It is reversible by an admin, so the confirm is the platform's
//    two-step ConfirmButton rather than a permanent-delete dialog, and the reason is REQUIRED
//    because "this was not a real visit" is a claim somebody has to stand behind.
//  - A "STICKER CONFIRMED" ROW IS THE THIRD STATE (Session AOE), not a variant of the other two.
//    It is the tap that proved a freshly fitted or rotated tag reads, so it is excluded from the
//    counts exactly as a void is, but for the opposite reason: nobody is rejecting it. It is shown
//    in its own right, upright and in the brand's technical blue, never struck through and never
//    green, and it carries no Void control, because the platform has already classified it and
//    voiding it would only add a rejection to something no count includes.
//  - AN ORDINARY "RECORDED" ROW CAN ALSO BE "CURRENTLY THE CONFIRMING TAP" (Session AOG), and both
//    are said at once rather than one masking the other. When the office voids the tap that was
//    standing as the tag's confirming evidence, the platform moves that role onto the next-earliest
//    surviving tap, which is an ordinary attendance and stays one: its own installConfirmation flag
//    is never rewritten. The extra line answers the question a cross-check would otherwise have no
//    way to answer: which row, right now, is the one actually load-bearing.
//  - SESSION APF: THE TAG BECAME AN ON/OFF PAIR. A tap now OPTIONALLY carries a direction ("arrival"
//    or "departure"), shown as a small plain label beside the recorded time. It is null on every tap
//    recorded before this session and on every tap made by opening the sticker's own page (which has
//    no way to state one), and a blank is exactly as ordinary here as a blank "Who" column is: no
//    label at all, never a placeholder like "Undirected".
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import type { AttendanceTapEvent, AttendanceTapEventsResponse } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { useAuth } from "../../lib/auth";
import { ConfirmButton } from "../../components/ConfirmButton";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { StatusPill } from "../../components/StatusPill";
import { Loader } from "../../components/Loader";
import { buttonClasses } from "../../components/Button";
import { formatDateTime } from "../../lib/format";

// The server's own fixed window (TAP_PAGE_SIZE in asset.routes.ts). Mirrored here only to work out
// how many pages `total` spans; the route ignores any size the client sends, so this can never
// disagree with what actually comes back, only with the page count shown.
const PAGE_SIZE = 20;

// Stored instants render as LONDON wall clock (the shared formatDateTime is pinned to Europe/London). A UTC
// render reads an hour early all summer, on the one number this whole table exists to show. (The same
// reason AttendanceTapPage does it.)

export function AttendanceTapHistory({ assetId }: { assetId: string }) {
  const { user } = useAuth();
  // Unvoid is admin only, matching the route (POST /:id/tap/events/:tapId/unvoid, adminGuard): it
  // puts a record back into every count and cross-check. Voiding stays office/admin.
  const isAdmin = user?.role === "admin";

  const [page, setPage] = useState(1);
  const [data, setData] = useState<AttendanceTapEventsResponse | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [voidTarget, setVoidTarget] = useState<AttendanceTapEvent | null>(null);
  // The row currently mid-write, so only that row's control disables rather than the whole table.
  const [busyId, setBusyId] = useState<string | null>(null);

  // One fetch used by both the page effect and the post-write refresh. The epoch guard is what
  // stops a slow page 1 landing on top of a fast page 2 (or on top of the list a void just
  // refreshed): only the most recently started load is allowed to write state.
  const epoch = useRef(0);
  const load = useCallback(
    async (p: number) => {
      const mine = ++epoch.current;
      try {
        const res = await api.assets.tapEvents(assetId, { page: p });
        if (epoch.current === mine) {
          setData(res);
          setLoadFailed(false);
        }
      } catch {
        if (epoch.current === mine) setLoadFailed(true);
      }
    },
    [assetId],
  );

  useEffect(() => {
    void load(page);
  }, [load, page]);

  async function confirmVoid(tap: AttendanceTapEvent, reason: string) {
    setBusyId(tap.id);
    setError(null);
    try {
      await api.assets.voidTap(assetId, tap.id, reason);
      setVoidTarget(null);
      await load(page);
    } catch (err) {
      setError(friendlyApiError(err, "Could not void this tap. Please try again."));
    } finally {
      setBusyId(null);
    }
  }

  async function unvoid(tap: AttendanceTapEvent) {
    setBusyId(tap.id);
    setError(null);
    try {
      await api.assets.unvoidTap(assetId, tap.id);
      await load(page);
    } catch (err) {
      setError(friendlyApiError(err, "Could not unvoid this tap. Please try again."));
    } finally {
      setBusyId(null);
    }
  }

  if (loadFailed) {
    return (
      <p role="alert" className="rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
        Could not load the tap history. Please try again.
      </p>
    );
  }

  if (!data) return <Loader shape="inline" label="Loading the tap history..." />;

  const pageCount = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  return (
    <div className="space-y-3">
      {error && (
        <p role="alert" className="rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {error}
        </p>
      )}

      {data.taps.length === 0 ? (
        <p className="text-sm text-ele-body opacity-70">
          No taps recorded for this lift yet. Taps appear here as soon as an engineer taps the motor room POP Tag.
        </p>
      ) : (
        <>
          {/* Platform table rule: .ele-list-table is fixed layout and truncates, so every column
              width is declared. The Status column is the one that must WRAP (a void reason is a
              sentence in the office's own words), so it takes the cell-wrap escape hatch; the
              actions column takes cell-actions so its buttons are never clipped. */}
          <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
            <table className="ele-list-table w-full text-left text-sm">
              <colgroup>
                <col style={{ width: "30%" }} />
                <col style={{ width: "24%" }} />
                <col style={{ width: "30%" }} />
                <col style={{ width: "16%" }} />
              </colgroup>
              <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
                <tr>
                  <th className="px-3 py-2">Time</th>
                  <th className="px-3 py-2">Who</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="cell-actions px-3 py-2 text-right">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.taps.map((tap) => (
                  <tr key={tap.id} className={`border-t border-ele-purple-accent ${tap.voided ? "opacity-70" : ""}`}>
                    <td className="px-3 py-2 align-top text-ele-strong">
                      <span className={tap.voided ? "line-through" : ""}>{formatDateTime(tap.tappedAt)}</span>
                      {/* Session APF: a small plain label beside the time for a directed tap, nothing
                          at all for one with no direction (the ordinary case for every tap before
                          this session, and for one made from the sticker's own page). */}
                      {tap.direction && (
                        <span className="ml-2 font-menu text-xs uppercase tracking-wide text-ele-body opacity-70">
                          {tap.direction === "arrival" ? "Arrival" : "Departure"}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      {tap.byUser ? (
                        <span className={tap.voided ? "line-through" : ""}>{tap.byUser}</span>
                      ) : (
                        // Not a warning and not an error: see the file header. Muted, never amber
                        // or red, so it does not read as something to chase.
                        <span className={`text-ele-body opacity-70 ${tap.voided ? "line-through" : ""}`}>Not signed in</span>
                      )}
                    </td>
                    <td className="cell-wrap px-3 py-2 align-top">
                      {/* THREE STATES, NOT TWO (Session AOE), and the order matters: a voided row is
                          reported as voided even if it was also a confirmation, because a void is
                          the office's own explicit judgement and must not be masked by a flag the
                          platform set. */}
                      {tap.voided ? (
                        <>
                          <StatusPill word="Voided" tone="grey" />
                          {tap.voidReason && <div className="mt-1 text-xs text-ele-body">{tap.voidReason}</div>}
                          {tap.voidedBy && <div className="text-xs text-ele-body opacity-70">Voided by {tap.voidedBy}</div>}
                        </>
                      ) : tap.installConfirmation ? (
                        // BLUE, NOT GREEN AND NOT GREY. Green would read as a recorded visit, which
                        // is the misreading this whole session exists to end; grey is the void tint
                        // and would read as a rejection, when nothing here was rejected. Blue is the
                        // brand's technical/reference colour, which is what this row is.
                        <>
                          <StatusPill word="Sticker confirmed" tone="blue" />
                          <div className="mt-1 text-xs text-ele-body">
                            The tap that proved the tag reads after it was fitted. Not a visit, and not counted.
                          </div>
                        </>
                      ) : (
                        <>
                          <StatusPill word="Recorded" tone="green" />
                          {/* Session AOG. An ordinary attendance can quietly become the tag's SOLE
                              surviving confirming evidence: recomputeAttendanceTapConfirmation moves
                              Asset.attendanceTapConfirmedAt onto whichever non-voided tap is now
                              earliest, and that row's own installConfirmation flag stays false for
                              ever, because it records what the tap WAS at write time, not what it is
                              standing in for now. A small secondary line, not a status change: this
                              row IS an ordinary recorded visit, and it also happens to be the reason
                              the tag currently reads confirmed. Both are true, so both are said. No
                              equivalent cue is needed on a "Sticker confirmed" row: that one already
                              says so correctly. */}
                          {tap.tappedAt === data.assetAttendanceTapConfirmedAt && (
                            <div className="mt-1 text-xs text-ele-body opacity-70">Currently the confirming tap.</div>
                          )}
                        </>
                      )}
                    </td>
                    <td className="cell-actions px-3 py-2 text-right align-top">
                      {tap.voided ? (
                        isAdmin ? (
                          <ConfirmButton
                            label="Unvoid"
                            confirmLabel="Confirm?"
                            busy={busyId === tap.id}
                            busyLabel="Unvoiding..."
                            onConfirm={() => unvoid(tap)}
                            className="font-menu text-xs tracking-wide text-ele-blue hover:underline"
                          />
                        ) : null
                      ) : tap.installConfirmation ? (
                        // NO VOID CONTROL, and this is the reason rather than an oversight. Void
                        // means "this was not a real visit", and the platform has already said that
                        // about this row: it is in no count, so pressing Void would move nothing.
                        // A control that changes nothing while looking like it corrects something is
                        // worse than no control, because the office would believe they had acted.
                        // The tag is unconfirmed by ROTATING it, which is a different action on the
                        // card above, and the row stays as the honest record of what happened.
                        <span className="text-xs text-ele-body opacity-60">Not counted</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setError(null);
                            setVoidTarget(tap);
                          }}
                          className="font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-danger"
                        >
                          Void
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pageCount > 1 && (
            <div className="flex items-center justify-between gap-3 text-sm">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="rounded-ele border border-ele-purple-accent px-3 py-1 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-green disabled:opacity-40"
              >
                Previous
              </button>
              <span className="text-xs text-ele-body">
                Page {page} of {pageCount} | {data.total} taps
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                disabled={page >= pageCount}
                className="rounded-ele border border-ele-purple-accent px-3 py-1 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-green disabled:opacity-40"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* The one line that stops a column of "Not signed in" reading as a fault. */}
      <p className="text-xs text-ele-body opacity-70">
        A tap records WHEN somebody attended. The signed log card in the motor room records WHO, as it always has.
      </p>

      {voidTarget && (
        <VoidTapModal
          tap={voidTarget}
          busy={busyId === voidTarget.id}
          onConfirm={(reason) => confirmVoid(voidTarget, reason)}
          onCancel={() => setVoidTarget(null)}
        />
      )}
    </div>
  );
}

// The void dialog. A reason is required by the route, so it is required here too: the confirm stays
// disabled until something is typed, rather than letting the office press a button that comes back
// 400. The confirm itself is the platform's two-step ConfirmButton (arms on the first press, fires
// on the second), never a native window.confirm or window.prompt.
function VoidTapModal({
  tap,
  busy,
  onConfirm,
  onCancel,
}: {
  tap: AttendanceTapEvent;
  busy: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState("");
  const trimmed = reason.trim();

  return (
    <ModalShell ariaLabel="Void this tap" onClose={() => !busy && onCancel()} width="max-w-md" panelClassName="p-5 text-left">
      <h2 className="font-heading text-lg text-ele-strong">Void the tap at {formatDateTime(tap.tappedAt)}?</h2>
      <p className="mt-2 text-sm text-ele-body">
        The tap stays on this list, struck through with your reason, and is left out of every count. An admin can undo it.
      </p>

      <label className="mt-4 block text-sm text-ele-body" htmlFor="void-tap-reason">
        Reason
      </label>
      <textarea
        id="void-tap-reason"
        autoFocus
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={3}
        maxLength={500}
        placeholder="For example: recorded while testing the tag, nobody attended."
        className="mt-1 w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
      />

      <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className={`rounded-ele border border-ele-purple-accent px-4 py-2 text-sm disabled:opacity-50 ${MODAL_ACTION_BTN}`}
        >
          Cancel
        </button>
        <ConfirmButton
          label="Void tap"
          confirmLabel="Confirm?"
          busy={busy}
          busyLabel="Voiding..."
          disabled={trimmed.length === 0}
          title={trimmed.length === 0 ? "Give a reason before voiding this tap." : undefined}
          onConfirm={() => onConfirm(trimmed)}
          className={buttonClasses("danger", "md", MODAL_ACTION_BTN)}
          armedClassName="ring-2 ring-ele-danger ring-offset-2 ring-offset-[var(--ele-shell)]"
        />
      </div>
    </ModalShell>
  );
}
